import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";
import Typography from "@mui/joy/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { NotificationService } from "../../common/notification";
import { Game } from "../../models/games";
import { AdminExternalProviderInfo } from "../../models/externalMarkets";
import { gamesListApi } from "../games/gamesApi";
import { formatInt } from "../bids/analytics/format";
import { extProvidersApi, extProviderToggleApi, extPurchaseReviewCountApi } from "./externalMarketsApi";
import { formatUsd, ReasonDialog } from "./components/shared";
import { MarketsTab } from "./tabs/MarketsTab";
import { ProfitRulesTab } from "./tabs/ProfitRulesTab";
import { AccountsTab } from "./tabs/AccountsTab";
import { PurchasesTab } from "./tabs/PurchasesTab";
import { AuditTab } from "./tabs/AuditTab";
import { SettingsTab } from "./tabs/SettingsTab";

const TABS = ["markets", "profit-rules", "accounts", "purchases", "audit", "settings"] as const;

const tabListSx = {
    pl: { xs: 0, md: 2 },
    justifyContent: "left",
    [`&& .${tabClasses.root}`]: {
        fontWeight: "600",
        flex: "initial",
        color: "text.tertiary",
        [`&.${tabClasses.selected}`]: {
            bgcolor: "transparent",
            color: "text.primary",
            "&::after": {
                height: "2px",
                bgcolor: "primary.500",
            },
        },
    },
} as const;

function tabFromHash(hash: string): number {
    const idx = TABS.indexOf((hash || "").replace("#", "") as (typeof TABS)[number]);
    return idx >= 0 ? idx : 0;
}

export function ExternalMarkets() {
    const navigate = useNavigate();
    const location = useLocation();
    const [providers, setProviders] = React.useState<AdminExternalProviderInfo[]>([]);
    const [providerKey, setProviderKey] = React.useState<string | null>(null);
    const [games, setGames] = React.useState<Game[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [toggle, setToggle] = React.useState<"enable" | "disable" | null>(null);
    const [reviewCount, setReviewCount] = React.useState(0);
    const tab = tabFromHash(location.hash);

    const loadProviders = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await extProvidersApi();
            if (res.result) {
                const list = res.value ?? [];
                setProviders(list);
                setProviderKey((current) =>
                    current && list.some((p) => p.key === current) ? current : list[0]?.key ?? null
                );
            } else {
                NotificationService.alertError(res.error ?? "Не удалось загрузить поставщиков");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadProviders().catch(console.error);
        gamesListApi()
            .then((res) => res.result && setGames(res.value ?? []))
            .catch(console.error);
    }, [loadProviders]);

    // Выкупы, ждущие ручного разбора: счётчик на вкладке журнала, чтобы их не пропустили
    React.useEffect(() => {
        setReviewCount(0);
        if (!providerKey) {
            return;
        }
        extPurchaseReviewCountApi(providerKey)
            .then((res) => res.result && setReviewCount(Number(res.value ?? 0)))
            .catch(console.error);
    }, [providerKey, tab]);

    const provider = providers.find((p) => p.key === providerKey) ?? null;
    const activeMarkets = provider?.markets.filter((m) => m.isActive).length ?? 0;
    const onSale = provider?.markets.reduce((sum, m) => sum + Number(m.activeListings ?? 0), 0) ?? 0;
    const failed24h = provider?.markets.reduce((sum, m) => sum + Number(m.failedPurchases24h ?? 0), 0) ?? 0;

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: {
                    xs: "calc(12px + var(--Header-height))",
                    sm: "calc(12px + var(--Header-height))",
                    md: 3,
                },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                height: "100dvh",
                gap: 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                <Typography level="h2" component="h1">
                    Поставщики
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<QueryStatsIcon />}
                    onClick={() => navigate("/app/external-markets/analytics")}
                >
                    Аналитика
                </Button>
            </Stack>

            {loading && providers.length === 0 ? (
                <CircularProgress />
            ) : !provider ? (
                <Typography level="body-md">Поставщики не подключены.</Typography>
            ) : (
                <>
                    <Card variant="outlined" sx={{ gap: 1.5 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
                            {providers.length > 1 && (
                                <FormControl size="sm" sx={{ minWidth: 180 }}>
                                    <FormLabel>Поставщик</FormLabel>
                                    <Select size="sm" value={providerKey} onChange={(_, v) => v && setProviderKey(v as string)}>
                                        {providers.map((p) => (
                                            <Option key={p.key} value={p.key}>
                                                {p.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <Box>
                                <Typography level="title-lg">{provider.name}</Typography>
                                <Typography level="body-xs">
                                    ключ {provider.key}, продавец{" "}
                                    <Link level="body-xs" onClick={() => navigate(`/app/users/${provider.systemUserId}`)}>
                                        {String(provider.systemUserId)}
                                    </Link>
                                </Typography>
                            </Box>
                            <Chip
                                variant="soft"
                                color={activeMarkets === 0 ? "danger" : activeMarkets < provider.markets.length ? "warning" : "success"}
                            >
                                Включено связок: {activeMarkets} из {provider.markets.length}
                            </Chip>
                            <Box>
                                <Typography level="body-xs">Баланс покупающих аккаунтов</Typography>
                                <Typography level="title-sm">
                                    {formatUsd(provider.balanceUsd)}
                                    {provider.maxAccountBalanceUsd != null && (
                                        <Typography level="body-xs"> (макс. на аккаунте {formatUsd(provider.maxAccountBalanceUsd)})</Typography>
                                    )}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs">На витрине</Typography>
                                <Typography level="title-sm">{formatInt(onSale)}</Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs">Провалы выкупа за 24ч</Typography>
                                <Typography level="title-sm" color={failed24h > 0 ? "danger" : undefined}>
                                    {formatInt(failed24h)}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Stack direction="row" spacing={1}>
                                <IconButton size="sm" variant="outlined" onClick={() => loadProviders()} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                                <Button
                                    size="sm"
                                    color="success"
                                    variant="outlined"
                                    disabled={activeMarkets === provider.markets.length}
                                    onClick={() => setToggle("enable")}
                                >
                                    Включить все
                                </Button>
                                <Button
                                    size="sm"
                                    color="danger"
                                    variant="outlined"
                                    disabled={activeMarkets === 0}
                                    onClick={() => setToggle("disable")}
                                >
                                    Выключить все
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>

                    <Tabs
                        value={tab}
                        onChange={(_, v) => navigate({ hash: `#${TABS[typeof v === "number" ? v : 0]}` })}
                        sx={{ bgcolor: "transparent", mt: 1 }}
                    >
                        <TabList size="sm" sx={tabListSx}>
                            <Tab value={0}>Связки с играми</Tab>
                            <Tab value={1}>Правила прибыли</Tab>
                            <Tab value={2}>Аккаунты и прокси</Tab>
                            <Tab value={3}>
                                Журнал выкупов
                                {reviewCount > 0 && (
                                    <Chip size="sm" color="warning" variant="solid" sx={{ ml: 1 }}
                                          title="Выкупы, требующие ручного разбора">
                                        {reviewCount}
                                    </Chip>
                                )}
                            </Tab>
                            <Tab value={4}>Журнал изменений</Tab>
                            <Tab value={5}>Настройки</Tab>
                        </TabList>
                        <TabPanel value={0} sx={{ px: 0 }}>
                            <MarketsTab provider={provider} games={games} onChanged={loadProviders} />
                        </TabPanel>
                        <TabPanel value={1} sx={{ px: 0 }}>
                            {tab === 1 && <ProfitRulesTab provider={provider} games={games} />}
                        </TabPanel>
                        <TabPanel value={2} sx={{ px: 0 }}>
                            {tab === 2 && <AccountsTab provider={provider} />}
                        </TabPanel>
                        <TabPanel value={3} sx={{ px: 0 }}>
                            {tab === 3 && <PurchasesTab provider={provider} games={games} />}
                        </TabPanel>
                        <TabPanel value={4} sx={{ px: 0 }}>
                            {tab === 4 && <AuditTab provider={provider} games={games} />}
                        </TabPanel>
                        <TabPanel value={5} sx={{ px: 0 }}>
                            {tab === 5 && <SettingsTab provider={provider} onChanged={loadProviders} />}
                        </TabPanel>
                    </Tabs>

                    <ReasonDialog
                        open={toggle !== null}
                        title={toggle === "enable" ? `Включить ${provider.name}` : `Выключить ${provider.name}`}
                        description={
                            toggle === "enable"
                                ? "Все связки поставщика будут включены, предметы вернутся в продажу."
                                : "Все связки поставщика будут выключены: предметы на паузе, новые покупки невозможны. Начатые выкупы доведутся до конца."
                        }
                        confirmLabel={toggle === "enable" ? "Включить" : "Выключить"}
                        confirmColor={toggle === "enable" ? "success" : "danger"}
                        reasonRequired
                        onClose={() => setToggle(null)}
                        onConfirm={async (reason) => {
                            const res = await extProviderToggleApi(provider.key, toggle === "enable", reason);
                            if (res.result) {
                                NotificationService.alertSuccess(toggle === "enable" ? "Поставщик включён" : "Поставщик выключен");
                                setToggle(null);
                                await loadProviders();
                            } else {
                                NotificationService.alertError(res.error ?? "Не удалось изменить состояние поставщика");
                            }
                        }}
                    />
                </>
            )}
        </Box>
    );
}

export default ExternalMarkets;
